import { CommonModule, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { OwnerStatusHistoryItem, OwnerStatusWidgetModel } from '@app/core/models';

@Component({
  selector: 'app-owner-status-card',
  standalone: true,
  imports: [CommonModule, DatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (widget && widget.status !== 'Live' && widget.status !== 'Completed') {
      <section
        class="border border-amber-200 bg-amber-50 p-5 text-amber-950 shadow-sm"
        [ngClass]="compact ? 'rounded-none border-x-0 border-t-0' : 'rounded-2xl'"
        role="status"
        aria-live="polite"
      >
        <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div class="min-w-0">
            <div class="flex flex-wrap items-center gap-2">
              <span class="rounded-full bg-white px-2.5 py-1 text-xs font-semibold text-amber-800">
                {{ widget.status }}
              </span>
              @if (!emailConfirmed) {
                <span class="rounded-full bg-rose-100 px-2.5 py-1 text-xs font-semibold text-rose-700">
                  Email not confirmed
                </span>
              }
            </div>
            <h2 class="mt-3 text-lg font-semibold text-slate-950">{{ widget.title }}</h2>
            <p class="mt-2 max-w-3xl text-sm leading-6 text-slate-700">{{ widget.message }}</p>
            @if (widget.lastUpdatedAt) {
              <p class="mt-3 text-xs font-medium uppercase tracking-[0.16em] text-slate-500">
                Last updated {{ widget.lastUpdatedAt | date: 'MMM d, y h:mm a' }}
              </p>
            }
          </div>

          <div class="flex shrink-0 flex-wrap gap-2">
            @if (!emailConfirmed) {
              <button
                type="button"
                class="rounded-xl border border-amber-300 bg-white px-3 py-2 text-sm font-semibold text-amber-900 disabled:cursor-not-allowed disabled:opacity-60"
                [disabled]="resendingConfirmation"
                (click)="resendConfirmation.emit()"
              >
                {{ resendingConfirmation ? 'Sending...' : 'Resend confirmation' }}
              </button>
            }
            @if (showSupportCta) {
              <a
                class="rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800"
                href="mailto:support@actionledger.test"
              >
                Contact support
              </a>
            }
          </div>
        </div>

        @if (widget.supportMessage || widget.nextOwnerAction || widget.nextAdminAction) {
          <div class="mt-4 grid gap-3 md:grid-cols-3">
            @if (widget.supportMessage) {
              <div class="rounded-xl bg-white/75 p-3 text-sm text-slate-700">{{ widget.supportMessage }}</div>
            }
            @if (widget.nextOwnerAction) {
              <div class="rounded-xl bg-white/75 p-3 text-sm text-slate-700">
                <span class="font-semibold">Owner:</span> {{ widget.nextOwnerAction }}
              </div>
            }
            @if (widget.nextAdminAction) {
              <div class="rounded-xl bg-white/75 p-3 text-sm text-slate-700">
                <span class="font-semibold">Admin:</span> {{ widget.nextAdminAction }}
              </div>
            }
          </div>
        }

        @if (history.length) {
          <div class="mt-4 border-t border-amber-200 pt-4">
            <p class="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Recent status history</p>
            <ol class="mt-3 space-y-2">
              @for (item of history.slice(0, 4); track item.id) {
                <li class="flex flex-wrap items-center gap-2 text-sm text-slate-700">
                  <span class="font-semibold text-slate-900">{{ item.status }}</span>
                  <span>{{ item.changedAt | date: 'MMM d, y h:mm a' }}</span>
                  @if (item.note) {
                    <span>{{ item.note }}</span>
                  }
                </li>
              }
            </ol>
          </div>
        }
      </section>
    }
  `
})
export class OwnerStatusCardComponent {
  @Input({ required: true }) widget: OwnerStatusWidgetModel | null = null;
  @Input() history: OwnerStatusHistoryItem[] = [];
  @Input() emailConfirmed = true;
  @Input() compact = false;
  @Input() resendingConfirmation = false;
  @Output() resendConfirmation = new EventEmitter<void>();

  get showSupportCta(): boolean {
    return this.widget?.status === 'SetupFailed' || this.widget?.status === 'Suspended';
  }
}
